import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import TonyteBackground from '../components/TonyteBackground';
import { Colors, Spacing, Typography } from '../theme';
import type { RootStackParamList } from '../types';
import { callVerifyStudentToken } from '../utils/studentVerification';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

type Props = NativeStackScreenProps<RootStackParamList, 'StudentVerificationComplete'>;

type Phase = 'loading' | 'verified' | 'pending' | 'expired' | 'invalid' | 'error';

// The Firebase callable-functions client falls back to the bare gRPC status
// code as both `code` and `message` (e.g. "internal", "unavailable") whenever
// it can't parse a proper error envelope out of the HTTP response — this
// happens on network failures, timeouts, or any non-callable-shaped error
// response, not just genuine server-side crashes. Never show these raw
// codes to the user; always fall back to a friendly message instead.
const RAW_FUNCTIONS_ERROR_CODES = new Set([
  'internal', 'unknown', 'unauthenticated', 'permission-denied', 'unavailable',
  'deadline-exceeded', 'cancelled', 'aborted', 'unimplemented', 'data-loss',
  'resource-exhausted', 'out-of-range', 'already-exists',
]);

function friendlyErrorMessage(rawMessage: string, fallback: string): string {
  const normalized = rawMessage.trim().toLowerCase();
  if (!normalized || RAW_FUNCTIONS_ERROR_CODES.has(normalized)) {
    return fallback;
  }
  return rawMessage;
}

export default function StudentVerificationCompleteScreen({ route, navigation }: Props) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createAppearanceStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const { token } = route.params;

  const [phase, setPhase] = useState<Phase>('loading');
  const [university, setUniversity] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setErrorMessage(t('verificationComplete.noToken'));
      return;
    }

    callVerifyStudentToken(token)
      .then(result => {
        setUniversity(result.university || '');
        const s = result.status;
        if (s === 'verified' || s === 'approved') {
          console.log('[Tonyte][StudentVerification]', 'studyZoneUnlocked');
          setPhase('verified');
        } else if (s === 'pending' || s === 'pending_admin_review') {
          setPhase('pending');
        } else {
          setPhase('error');
          setErrorMessage(result.message || t('verificationComplete.couldNotComplete'));
        }
      })
      .catch(err => {
        const code: string = (err as any)?.code ?? '';
        const msg: string = (err as any)?.message ?? '';
        console.error('[Tonyte][StudentVerification] verify call failed', { code, msg, details: (err as any)?.details });
        if (code === 'functions/failed-precondition' || msg.toLowerCase().includes('expired')) {
          setPhase('expired');
          setErrorMessage(t('verificationComplete.expiredBody'));
        } else if (code === 'functions/not-found') {
          setPhase('invalid');
          setErrorMessage(t('verificationComplete.invalidBody'));
        } else {
          setPhase('error');
          setErrorMessage(friendlyErrorMessage(msg, t('common.somethingWentWrong')));
        }
      });
  }, [t, token]);

  const icon = phase === 'verified'
    ? { name: 'check-circle', color: Colors.turquoise }
    : phase === 'pending'
      ? { name: 'clock', color: 'rgba(255,200,60,0.95)' }
      : { name: 'x-circle', color: 'rgba(255,70,70,0.9)' };

  const title = {
    loading: t('verificationComplete.verifying'),
    verified: t('verificationComplete.verified'),
    pending: t('verificationComplete.underReview'),
    expired: t('verificationComplete.expired'),
    invalid: t('verificationComplete.invalid'),
    error: t('verificationComplete.failed'),
  }[phase];

  const body = {
    loading: t('verificationComplete.checking'),
    verified: university
      ? t('verificationComplete.verifiedUniversity').replace('{university}', university)
      : t('verificationComplete.verifiedWelcome'),
    pending:
      t('verificationComplete.pendingBody'),
    expired: errorMessage,
    invalid: errorMessage,
    error: errorMessage,
  }[phase];

  return (
    <TonyteBackground appearanceAware>
      <View style={[styles.container, appearance.container, { paddingTop: insets.top + 40, paddingBottom: insets.bottom + 24 }]}> 
        {phase === 'loading' ? (
          <ActivityIndicator size="large" color={Colors.turquoise} style={styles.spinner} />
        ) : (
          <Icon name={icon.name} size={64} color={icon.color} style={styles.icon} />
        )}

        <Text style={[styles.title, appearance.textPrimary]}>{title}</Text>
        <Text style={[styles.body, appearance.textSecondary]}>{body}</Text>

        {phase !== 'loading' ? (
          <TouchableOpacity
            activeOpacity={0.82}
            style={styles.button}
            onPress={() => {
              if (navigation.canGoBack()) {
                navigation.goBack();
              } else {
                navigation.navigate('Main');
              }
            }}
          >
            <Text style={styles.buttonText}>{t('verificationComplete.back')}</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </TonyteBackground>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.xl,
  },
  spinner: {
    marginBottom: Spacing.lg,
  },
  icon: {
    marginBottom: Spacing.lg,
  },
  title: {
    color: Colors.textPrimary,
    fontSize: 24,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
    marginBottom: Spacing.sm,
  },
  body: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: Spacing.xl,
  },
  button: {
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 28,
    backgroundColor: Colors.turquoise,
  },
  buttonText: {
    color: '#02050b',
    fontSize: 15,
    fontWeight: Typography.extrabold,
  },
});

const createAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
});
